import { Info, Pencil } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { TOOLTIP_DELAY_MS } from '@/constants/timing'
import { cn } from '@/lib/utils'
import type { ColumnMeta } from './types'

interface ColumnHeaderContentProps {
  displayName: string
  meta?: ColumnMeta
  className?: string
}

const MANUAL_SOURCE_CODE = 'MANUAL'

function formatSourceLabel(dataSource?: ColumnMeta['dataSource']): string {
  if (!dataSource) return 'External Source'

  return dataSource
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

export function ColumnHeaderContent({
  displayName,
  meta,
  className,
}: ColumnHeaderContentProps) {
  const dataSource = meta?.dataSource
  const sourceLabel = formatSourceLabel(dataSource)
  const hasExternalSource = Boolean(
    dataSource && dataSource !== MANUAL_SOURCE_CODE
  )
  const showPencilIndicator = !hasExternalSource && meta?.isEditable === true

  return (
    <span
      className={cn(
        'inline-flex w-full items-center gap-1.5 truncate',
        className
      )}
      title={displayName}
    >
      <span className="truncate">{displayName}</span>
      {hasExternalSource && (
        <Tooltip delayDuration={TOOLTIP_DELAY_MS}>
          <TooltipTrigger asChild>
            <span
              className="ml-auto inline-flex shrink-0 p-0.5 text-primary/70"
              onClick={(e) => e.stopPropagation()}
            >
              <Info className="size-3" aria-hidden />
            </span>
          </TooltipTrigger>
          <TooltipContent side="top">
            <p>{`Imported from ${sourceLabel}`}</p>
          </TooltipContent>
        </Tooltip>
      )}
      {showPencilIndicator && (
        <Tooltip delayDuration={TOOLTIP_DELAY_MS}>
          <TooltipTrigger asChild>
            <span
              className="ml-auto inline-flex shrink-0 p-0.5 text-primary/70"
              onClick={(e) => e.stopPropagation()}
            >
              <Pencil className="size-3" aria-hidden />
            </span>
          </TooltipTrigger>
          <TooltipContent side="top">
            <p>Editable field</p>
          </TooltipContent>
        </Tooltip>
      )}
    </span>
  )
}
